/**
 * What went wrong with an LLM call, and how wide the damage is.
 *
 * Two independent axes, because the pair — not either half — is what decides a
 * policy. "Rate limited on one model" and "rate limited on the whole account"
 * share a class and call for opposite actions; "the account cannot spend" and
 * "the key is invalid" share a scope and call for different remedies.
 *
 * Both are declared as `as const` arrays with the union derived from them, not
 * as TypeScript `enum`s. An `enum` is NOMINAL, so a value arriving as a plain
 * string — off the wire, out of a JSON column, from a test fixture — is not
 * assignable to it without a cast, and the cast is where the type safety goes.
 * The array also gives a runtime list, which is what lets a test assert the
 * policy table covers every member instead of restating the members by hand.
 */

/**
 * The five failure classes. Each one earns its place by carrying a policy
 * consequence no other class carries — see LLM_FAILURE_POLICY.
 *
 * Deliberately NOT a sixth class for "we could not tell". An unrecognised
 * failure is the absence of a classification, and `classifyLlmFailure` returns
 * `null` for it. Inventing a class would give the unknown a policy, which is
 * the one thing that must never be inferred.
 */
export const LLM_FAILURE_CLASSES = [
  /**
   * The account is barred from spending — a spent balance, a hard billing
   * limit, a disabled billing project. Distinguished from RATE_LIMITED by the
   * remedy, which is the only distinction that matters: this one clears when a
   * HUMAN acts, and until they do every call to the provider fails.
   */
  "ACCOUNT_CANNOT_SPEND",
  /** The credential was rejected. Retrying replays the same rejected key. */
  "AUTH_INVALID",
  /** Throughput exceeded. Clears itself on a short window with no human. */
  "RATE_LIMITED",
  /** The provider failed in a way that may not recur — timeout, overload, 5xx. */
  "PROVIDER_TRANSIENT",
  /** The request itself is malformed. Retrying replays the identical bad request. */
  "REQUEST_INVALID"
] as const;

export type LlmFailureClass = (typeof LLM_FAILURE_CLASSES)[number];

/**
 * How wide the failure is — the blast radius, not the cause.
 *
 * A classifier that sees ONE error can only ever report the NARROWEST scope
 * that error proves. Widening ("every model is failing", "the whole account is
 * down") needs many observations over time, which is a health-aware judgement
 * and deliberately not made here.
 */
export const LLM_FAILURE_SCOPES = [
  /** Everything under this provider account is affected. */
  "ACCOUNT",
  /** One model is affected; siblings on the same account may be healthy. */
  "MODEL",
  /** One provider region or endpoint is affected. */
  "REGION",
  /** Only this one request is affected. Another identical caller may succeed. */
  "REQUEST"
] as const;

export type LlmFailureScope = (typeof LLM_FAILURE_SCOPES)[number];

/** A classified LLM failure: what broke, and how wide. */
export interface LlmFailure {
  readonly failureClass: LlmFailureClass;
  readonly scope: LlmFailureScope;
}
